import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider } from '@clerk/clerk-react';
import { Toaster } from 'react-hot-toast';
import { DevAuthProvider } from './contexts/DevAuthContext';
import { ThemeProvider } from './context/ThemeContext';
import App from './App';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Check if we have valid Clerk keys (real keys start with pk_test_ or pk_live_ and are longer)
const hasValidClerkKey = clerkPubKey && clerkPubKey.startsWith('pk_') && clerkPubKey.length > 30;

// Development mode warning
if (!hasValidClerkKey) {
  console.log('%c[DEV MODE] Running with development authentication', 'color: #EAB308; font-weight: bold');
  console.log('%cSet VITE_CLERK_PUBLISHABLE_KEY in .env for production', 'color: #94A3B8');
}

function AuthProvider({ children }) {
  if (hasValidClerkKey) {
    return <ClerkProvider publishableKey={clerkPubKey}>{children}</ClerkProvider>;
  }
  return <DevAuthProvider>{children}</DevAuthProvider>;
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'hsl(var(--card))',
                  color: 'hsl(var(--foreground))',
                  border: '1px solid hsl(var(--border))',
                },
                success: {
                  iconTheme: {
                    primary: 'hsl(var(--success))',
                    secondary: 'hsl(var(--foreground))',
                  },
                },
                error: {
                  iconTheme: {
                    primary: 'hsl(var(--destructive))',
                    secondary: 'hsl(var(--foreground))',
                  },
                },
              }}
            />
          </BrowserRouter>
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>
);
